import { SwitchButton } from '@components/core-ui/form-elements/buttons/Button/variations/SwitchButton';
import { HeaderSwitchWrapper } from '@components/Header/components/HeaderSwitchWrapper';
import { BarElement, CategoryScale, Chart as ChartJS, Filler, LinearScale, LineElement, PointElement } from 'chart.js';
import { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, PointElement, LineElement, LinearScale, BarElement, Filler);

type DataItem = {
  date?: string;
  value?: number;
};

type TProps = {
  data: {
    week?: DataItem[];
    month?: DataItem[];
    totalJobs?: number;
  };
};

export const PerformanceReview = ({ data }: TProps) => {
  const [period, setPeriod] = useState<'7days' | '30days'>('7days');

  const isDark = document.documentElement.dataset.theme === 'dark';
  const chartTheme = useMemo(() => {
    return {
      muted: isDark ? '#9aa7c1' : '#858dbd',
      accent: isDark ? '#8ab8ff' : '#7CB1FE',
      accentStrong: isDark ? '#6ea2ff' : '#7CB1FE',
      grid: isDark ? '#263149' : '#d5def2',
      point: isDark ? '#151e2f' : 'var(--color-surface)',
      fill: isDark ? 'rgba(138, 184, 255, 0.18)' : 'rgba(124, 177, 254, 0.1)',
    };
  }, [isDark]);

  ChartJS.defaults.color = chartTheme.muted;
  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      borderRadius: 2,
      borderWidth: 4,
      lineTension: 0.4,
      borderColor: chartTheme.accent,
      pointHoverRadius: 8,
      pointBorderWidth: 3,
      pointHoverBorderWidth: 3,
      pointBackgroundColor: chartTheme.point,
      pointHoverBackgroundColor: chartTheme.point,
      pointBorderColor: chartTheme.accentStrong,
      pointHoverBorderColor: chartTheme.accent,
      animation: {
        duration: 0,
      },
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: chartTheme.grid,
            tickBorderDash: [2, 4],
          },
          border: {
            display: false,
            dash: [2, 4],
          },
        },
      },
    }),
    [chartTheme],
  );

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const periodData = period === '7days' ? data.week || [] : data.month || [];
  const labels = periodData.map((item) => (item.date ? weekDays[new Date(item.date).getDay()] : ''));

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Jobs completed',
        data: periodData.map((item) => item.value ?? 0),
        fill: true,
        backgroundColor: chartTheme.fill,
        pointRadius: 4,
      },
    ],
  };

  return (
    <div>
      <div className="mt-3 mb-3 flex min-h-11 flex-wrap items-center">
        <HeaderSwitchWrapper key="tabSwitcher">
          <SwitchButton active={period === '7days'} onClick={() => setPeriod('7days')}>
            Last 7 days
          </SwitchButton>
          <SwitchButton active={period === '30days'} onClick={() => setPeriod('30days')}>
            Last 30 days
          </SwitchButton>
        </HeaderSwitchWrapper>
      </div>
      <div className="pt-0.5 pb-2.5 text-xs leading-none font-normal text-foreground-muted">
        Total jobs: <span className="font-semibold">{data.totalJobs || 0}</span>
      </div>
      <div className="h-[214px] pt-2.5">
        <Line options={options} data={chartData} />
      </div>
    </div>
  );
};
